import { useLayoutEffect, useRef, useSyncExternalStore } from "react";
import type { ApiClient, CustomerOverview, TileKey, TileLayout, TileLayoutView } from "@kundeninfo/api-client";
import type { KachelwandAnordnung } from "@kundeninfo/ui/me-design";
import { TILE_CATALOG, TILE_ID_PREFIX } from "./tileCatalog.js";

/*
 * Die Anordnung der Kachelwand je Benutzer (380, 384): was die Wand meldet, in die gespeicherte Anordnung einfügen und
 * speichern. Die Wand kennt nur die Kacheln, die gezeichnet sind; der Monitor fehlt bei Kunden ohne Mandant und behält
 * trotzdem seinen Platz.
 */

/** Die Ansicht der Übersicht im Pfad der Anordnung — die einzige Stelle im Web; den Typ gibt der Vertrag (`TileLayoutView`). */
export const TILE_LAYOUT_VIEW: TileLayoutView = "customer-overview";

const unique = <T,>(items: readonly T[]): T[] => [...new Set(items)];

/** Der Schlüssel einer Kachel aus ihrer Kennung im Dokument; alles Unbekannte ist `null`. */
export function keyFromTileId(id: string): TileKey | null {
  if (!id.startsWith(TILE_ID_PREFIX)) return null;
  const key = id.slice(TILE_ID_PREFIX.length);
  return Object.hasOwn(TILE_CATALOG, key) ? key as TileKey : null;
}

/**
 * Fügt die Meldung der Wand in die gespeicherte Anordnung ein: Die gezeigten Kacheln nehmen die Plätze der gezeigten ein, in der
 * Folge der Meldung; jede nicht gezeigte Kachel behält Platz und Ausblendung. Eine gezeigte Kachel, die die gespeicherte
 * Anordnung nicht kennt, kommt ans Ende. `keyOf` liest den Schlüssel aus der Kennung im Dokument, alles Fremde ist `null`.
 */
export function mergeLayoutBy<K extends string>(saved: { order: K[]; hidden: K[] },
    change: { reihenfolge: readonly string[]; ausgeblendet: readonly string[] }, keyOf: (id: string) => K | null): { order: K[]; hidden: K[] } {
  const keysOf = (ids: readonly string[]): K[] => unique(ids.map(keyOf).filter((key): key is K => key !== null));
  const shown = keysOf(change.reihenfolge);
  const isShown = new Set(shown);
  const queue = [...shown];
  const order = saved.order.map((key) => (isShown.has(key) ? queue.shift()! : key));
  const hidden = unique([...saved.hidden.filter((key) => !isShown.has(key)), ...keysOf(change.ausgeblendet)]);
  return { order: [...order, ...queue], hidden };
}

/** `mergeLayoutBy` mit den Kennungen der Übersicht. */
export function mergeLayout(saved: TileLayout,
    change: { reihenfolge: readonly string[]; ausgeblendet: readonly string[] }): TileLayout {
  return mergeLayoutBy(saved, change, keyFromTileId);
}

/**
 * Die Kacheln, die die Wand zeichnet, in der Folge der gespeicherten Anordnung: dem Katalog bekannt, für diese Akte freigegeben
 * (`availableTiles`, P14) und — außer dem Monitor, der für sich lädt — mit einem Abschnitt in der Antwort. Der Server liefert die
 * ganze Anordnung (auch Kacheln anderer Rollen, Ruling W1); die nicht gezeichneten behalten über `mergeLayout` Platz und Ausblendung.
 */
export function visibleOrder(layout: TileLayout, available: readonly TileKey[], tiles: CustomerOverview["tiles"]): TileKey[] {
  return layout.order.filter((key) => Object.hasOwn(TILE_CATALOG, key) && available.includes(key)
    && (key === "monitor" || tiles[key] !== undefined));
}

/** Was gescheitert ist, bestimmt den Satz der Meldung: Speichern („gilt bis zum Neuladen“) oder Zurücksetzen (nichts geändert). */
export type WriteKind = "save" | "reset";
/** `kind: null`: Den Fehler meldet der Aufrufer selbst (Rückgabe von `write`), der Stand merkt ihn sich nicht. */
type Write<S> = { id: number; kind: WriteKind | null; send: () => Promise<Partial<S>>; settle: (answered: boolean) => void };

/**
 * Der Kern eines Anordnungsspeichers: Stand, Halter und Schreibschlange, für Übersicht und Cockpit gleich. Ein Speicher lebt je Klient (eine
 * Anmeldung), nicht je Zeichnung der Ansicht: Sie wird bei jedem Wechsel abgebaut und neu gebaut; Entwurf, Fehlermeldung und Schreibschlange
 * überleben das, und zwei Zeichnungen schreiben nie zugleich.
 *
 * Die Schreibungen sind serialisiert: höchstens eine ist unterwegs, von den wartenden zählt nur die neueste (jede trägt die ganze
 * Anordnung). So kommt beim Server an, was der Benutzer zuletzt getan hat, und nur die Antwort der jüngsten Schreibung zählt —
 * eine ältere überschreibt weder den Stand noch die Meldung. Scheitert die jüngste, bleibt der Entwurf stehen. Eine Änderung
 * während eines Zurücksetzens wartet dessen Antwort ab und geht dann als normale Speicherung hinaus; sie gilt, das Zurücksetzen
 * ist überholt.
 *
 * `write` liefert, ob der Server geantwortet hat; `false` heißt gescheitert oder ungesendet überholt.
 */
export function createLayoutStore<S extends { error: WriteKind | null }>(initial: S) {
  let state = initial;
  const listeners = new Set<() => void>();
  const holders = new Set<(changedId?: string) => void>();
  let run = 0;
  let busy = false;
  let waiting: Write<S> | null = null;

  // Die Halter laufen vor jedem sichtbaren Wechsel (Anordnung oder Meldung): Die Ansicht merkt dort die Lage im Bild. Das ist
  // Zugabe — wirft ein Halter, gilt der Wechsel trotzdem.
  const update = (patch: Partial<S>, changedId?: string): void => {
    const next = { ...state, ...patch };
    if ((Object.keys(patch) as (keyof S)[]).every((key) => next[key] === state[key])) return;
    for (const hold of holders) {
      try { hold(changedId); } catch { /* ohne gemerkte Lage rollt nur nichts nach */ }
    }
    state = next;
    for (const listener of listeners) listener();
  };

  // Die Schlange läuft, bis nichts mehr wartet; `busy` fällt in jedem Fall zurück, sonst stünde jede spätere Schreibung bis zum
  // Neuladen der Seite.
  const pump = async (): Promise<void> => {
    if (busy) return;
    busy = true;
    try {
      for (let next = waiting; next !== null; next = waiting) {
        waiting = null;
        let answered = false;
        try {
          const patch = await next.send();
          answered = true;
          if (next.id === run) update({ ...patch, error: null } as Partial<S>);
        } catch {
          if (next.id === run && next.kind !== null) update({ error: next.kind } as Partial<S>);
        }
        next.settle(answered);
      }
    } finally {
      busy = false;
    }
  };

  return {
    getState: (): S => state,
    subscribe: (listener: () => void): (() => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    addHolder: (hold: (changedId?: string) => void): (() => void) => { holders.add(hold); return () => { holders.delete(hold); }; },
    update,
    write: (kind: WriteKind | null, send: () => Promise<Partial<S>>): Promise<boolean> => new Promise((settle) => {
      waiting?.settle(false);
      waiting = { id: ++run, kind, send, settle };
      void pump();
    }),
    /** Ist eine Schreibung unterwegs oder wartend, deren Ausgang die nächste überholen kann? */
    isBusy: (): boolean => busy,
  };
}

/** Ein Speicher je Klient: Die Ansicht wird abgebaut und neu gebaut, der Speicher bleibt (`createLayoutStore`). */
export function perClient<C extends object, S>(create: (client: C) => S): (client: C) => S {
  const stores = new WeakMap<C, S>();
  return (client) => {
    let store = stores.get(client);
    if (store === undefined) stores.set(client, store = create(client));
    return store;
  };
}

/** Meldet die Ansicht beim Speicher an: `beforeReplace` läuft vor jedem sichtbaren Wechsel und ist immer die jüngste Fassung. */
export function useLayoutHolder(store: { addHolder: (hold: (changedId?: string) => void) => () => void },
    beforeReplace?: (changedId?: string) => void): void {
  const before = useRef(beforeReplace);
  before.current = beforeReplace;
  useLayoutEffect(() => store.addHolder((changedId) => before.current?.(changedId)), [store]);
}

type StoreState = { layout: TileLayout | null; error: WriteKind | null };

/**
 * Der Speicher der Anordnung eines Benutzers der Übersicht (`createLayoutStore`). `layout` ist der Stand des Benutzers: sein Entwurf, nach
 * der Antwort des Servers dessen Fassung (normalisiert); `null`, bis er etwas getan hat — dann gilt die Anordnung des Abrufs. Er bleibt bis
 * zum Neuladen der Seite (die Anordnung gehört dem Benutzer; eine Änderung von einem anderen Gerät zeigt erst das Neuladen).
 */
function createStore(client: Pick<ApiClient, "saveTileLayout" | "resetTileLayout">) {
  const { update, write, ...store } = createLayoutStore<StoreState>({ layout: null, error: null });
  return {
    ...store,
    /** Fügt die Meldung der Wand in den Stand des Benutzers ein (ohne ihn: in `fromServer`), zeigt sie sofort und speichert sie. */
    change(fromServer: TileLayout | null, c: KachelwandAnordnung): void {
      const base = store.getState().layout ?? fromServer;
      if (base === null) return;
      const next = mergeLayout(base, c);
      update({ layout: next }, c.id);
      void write("save", async () => ({ layout: await client.saveTileLayout(TILE_LAYOUT_VIEW, next) }));
    },
    reset(): void { void write("reset", async () => ({ layout: await client.resetTileLayout(TILE_LAYOUT_VIEW) })); },
  };
}
const storeOf = perClient(createStore);

/**
 * Die Anordnung der Übersicht: Sie folgt dem Server (`fromServer`), bis der Benutzer etwas ändert; dann gilt sofort sein Stand
 * (`layout`), und die ganze Anordnung geht an den Server (siehe `createStore`).
 *
 * `beforeReplace` läuft unmittelbar vor jedem Wechsel des Stands oder der Meldung (`changedId`: die bewegte Kachel, sonst keine):
 * Der Aufrufer merkt sich dort die Lage im Bild.
 */
export function useTileLayout(client: Pick<ApiClient, "saveTileLayout" | "resetTileLayout">, fromServer: TileLayout | null,
    beforeReplace?: (changedId?: string) => void): {
    layout: TileLayout | null; change: (c: KachelwandAnordnung) => void; reset: () => void; error: WriteKind | null } {
  const store = storeOf(client);
  const { layout, error } = useSyncExternalStore(store.subscribe, store.getState);
  useLayoutHolder(store, beforeReplace);
  return { layout: layout ?? fromServer, change: (c) => store.change(fromServer, c), reset: store.reset, error };
}
